import { AvailableIcons } from "~/components/ui/Icon"
import { FilterToggleValue } from "@decocms/apps-commerce/types"
import vtexProductListingPage from "@decocms/apps-vtex/inline-loaders/productListingPage"

import Breadcrumb from "~/components/ui/Breadcrumb"
import Icon from "~/components/ui/Icon"
import { SITE } from "~/constants"

interface FilterFull extends FilterToggleValue {
  icon: AvailableIcons
  iconHover: AvailableIcons
  color?: string
}

/**
 * @title {{nameFilter}}
 */
export interface Filter {
  /**
   * @title Nome do filtro
   * @description Coloque o nome da categoria ou da subcategoria que deseja que seja um filtro
   */
  nameFilter: string

  /**
   * @title Categoria ou subcategoria?
   * @description Selecione se esse filtro é de uma categoria ou de uma subcategoria
   */
  categoryOrSubcategory: 'Categoria' | 'Subcategoria'

  /**
   * @title Ícone do filtro
   */
  icon: AvailableIcons

  /**
   * @title Ícone do filtro - hover do mouse
   */
  iconHover: AvailableIcons

  /**
   * @title Cor do filtro
   * @format color
   */
  color?: string
}

export interface Props {
  /**
   * @title Nome da marca
   * @description Coloque o nome da marca, exatamente igual é colocado no SelectedFacet da section BrandProducts. Exemplo: la-pimienta OU my-vib
   */
  nameBrand: string

  /**
   * @title Filtros
   */
  filters: Filter[]
}

function BrandFilter({ currentFilters, url, nameBrand }: Awaited<ReturnType<typeof loader>>) {
  if (!currentFilters?.length) return null

  const breadcrumb = [
    {
      '@type': 'ListItem' as const,
      item: url.pathname,
      name: nameBrand.replaceAll('-', ' ').toUpperCase(),
      position: 0,
    },
  ]

  return (
    <div className='w-full max-w-[1834px] px-4 mx-auto mt-5 lg:mt-8 flex flex-col gap-5 lg:gap-8'>
      <ul className='grid grid-cols-2 lg:flex items-center gap-x-3 gap-y-2 lg:gap-y-3 xl:gap-x-4 xl:gap-y-4'>
        {currentFilters.map((filter) => (
          <li key={`filter-${filter.label}`} className='flex-1'>
            <a
              href={filter.url}
              className='min-h-12 w-full h-full rounded-lg border group relative flex overflow-hidden'
              style={{ borderColor: filter.color }}
            >
              <div
                className='px-1 text-center absolute w-full h-full flex items-center justify-center gap-2 text-sm xl:text-base font-bold duration-300 group-hover:opacity-0'
                style={{ color: filter.color }}
              >
                <Icon size={30} id={filter.icon as AvailableIcons} aria-label={filter.label} />
                {filter.label}
              </div>

              <div
                className='px-1 text-center absolute w-full h-full flex items-center justify-center gap-2 text-sm xl:text-base font-bold duration-300 text-white opacity-0 group-hover:opacity-100'
                style={{ backgroundColor: filter.color }}
              >
                <Icon size={30} id={filter.iconHover as AvailableIcons} aria-label={filter.label} />
                {filter.label}
              </div>
            </a>
          </li>
        ))}

        <li className='flex-1'>
          <a
            className='px-1 text-center duration-300 min-h-12 flex items-center justify-center gap-2 w-full h-full rounded-lg border text-sm xl:text-base font-bold border-[#FFBB39] text-[#FFBB39] hover:bg-[#FFBB39] hover:text-[#ffffff]'
            href={url.pathname}
          >
            ver todos
          </a>
        </li>

        <li className='flex-1'>
          <a
            className='px-1 text-center capitalize duration-300 min-h-12 flex items-center justify-center gap-2 w-full h-full rounded-lg border text-sm xl:text-base font-bold border-[#DF368E] text-[#DF368E] hover:bg-[#DF368E] hover:text-[#ffffff]'
            href='#form-resell'
          >
            {`Revender ${nameBrand.replaceAll('-', ' ')}`}
          </a>
        </li>
      </ul>

      <Breadcrumb itemListElement={breadcrumb} />
    </div>
  )
}

export const loader = async (props: Props & { __pageUrl?: string }) => {
  const newSelectedFacets = [{ key: 'b', value: props.nameBrand }]

  const url = new URL(props.__pageUrl ?? "/", SITE.URL)

  // `pageHref` was the deco-cx/apps loader API; the inline loader takes
  // `__pagePath`, used as the base path of every `filter.url` it builds.
  // Search params are intentionally NOT forwarded (parity with the legacy
  // loader, which stripped them) so the filter list ignores current
  // sort/pagination.
  const page = await vtexProductListingPage(
    {
      selectedFacets: newSelectedFacets,
      __pagePath: url.pathname,
    } as never,
  )

  const filtersSection = props.filters

  const currentFilters = filtersSection?.reduce((acc, filterSection) => {
    const findCategory = page?.filters?.find((filterPage) =>
      filterPage.label === filterSection.categoryOrSubcategory
    )
    if (!findCategory || !Array.isArray(findCategory?.values)) return acc

    const filter = findCategory.values?.find((value) => value.label === filterSection.nameFilter)
    if (!filter) return acc

    return acc = [...acc, {
      ...filter,
      icon: filterSection.icon,
      iconHover: filterSection.iconHover,
      color: filterSection.color ?? '#000000',
    }]
  }, [] as FilterFull[])

  return {
    ...props,
    currentFilters,
    url,
  }
}
export default BrandFilter
